<script setup lang="ts">
import { computed, ref } from 'vue'
import { allowsTyped, cleanMoney, formatMoney } from '@/lib/inputFormat'

// v-model wrapper for a whole-number or money field. While focused it shows
// the raw draft the visitor is typing; once left it shows the formatted
// value, so `1250` reads as `1,250` without fighting the caret while typing.
defineOptions({ inheritAttrs: false })
const props = defineProps<{ modelValue: unknown; kind: 'money' | 'whole' }>()
const emit = defineEmits<{ 'update:modelValue': [value: unknown] }>()

// The stored value as text: a number or string as typed, anything else empty.
function asText(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

const focused = ref(false)
const draft = ref('')
const display = computed(() => {
  if (focused.value) return draft.value
  return props.kind === 'money' ? formatMoney(props.modelValue) : asText(props.modelValue)
})

function onFocus() {
  draft.value = asText(props.modelValue)
  focused.value = true
}
function onBlur() {
  focused.value = false
}
function onBeforeInput(event: Event) {
  const input = event as InputEvent
  if (input.inputType === 'insertText' && !allowsTyped(props.kind, input.data))
    event.preventDefault()
}
function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value
  draft.value = value
  emit('update:modelValue', props.kind === 'money' ? cleanMoney(value) : value)
}
</script>

<template>
  <span class="amount-input" :class="{ money: kind === 'money' }">
    <span v-if="kind === 'money'" class="prefix" aria-hidden="true">$</span>
    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -- every use wraps this component in a <label> -->
    <input
      v-bind="$attrs"
      type="text"
      :inputmode="kind === 'money' ? 'decimal' : 'numeric'"
      autocomplete="off"
      spellcheck="false"
      data-tidy="none"
      :value="display"
      @focus="onFocus"
      @blur="onBlur"
      @beforeinput="onBeforeInput"
      @input="onInput"
    />
  </span>
</template>

<style scoped>
.amount-input {
  position: relative;
  display: block;
}
.amount-input input {
  box-sizing: border-box;
  width: 100%;
  padding: 0.62rem 0.7rem;
  border: 1px solid #aeb9ac;
  border-radius: 3px;
  background: #fff;
  color: var(--ink);
  font: inherit;
  font-weight: 400;
}
.amount-input input:focus-visible {
  outline: 3px solid #b9c967;
  outline-offset: 2px;
  border-color: var(--pine);
}
.amount-input input[aria-invalid='true'] {
  border-color: #a53c28;
  box-shadow: 0 0 0 1px #a53c28;
}
.amount-input.money .prefix {
  position: absolute;
  left: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  color: #526057;
  font-weight: 400;
  pointer-events: none;
}
.amount-input.money input {
  padding-left: 1.35rem;
}
</style>
